import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { GiEntryDoor } from "react-icons/gi";
import { BiMailSend } from "react-icons/bi";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { ResultCard, TurnInfo, Player, CtaCard } from "../../shared/interfaces/GameLogicTypes";
import CommunityApi from "../../shared/api/CommunityApi";
import { track } from "../../shared/analytics";
import ResultCardGrid from "../../components/ResultCardGrid/ResultCardGrid";
import GameReview from "../../components/GameReview/GameReview";
import RateReviewIcon from "@mui/icons-material/RateReview";
import Feedback from "../../shared/api/FeedbackApi";
import StyleIcon from "@mui/icons-material/Style";
import PeopleIcon from "@mui/icons-material/People";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CampaignIcon from "@mui/icons-material/Campaign";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShareIcon from "@mui/icons-material/Share";
import { serverUrl } from "../../shared/runtime";

const ICON_MAP: Record<string, React.ReactNode> = {
  style:          <StyleIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  people:         <PeopleIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  tiktok:         <Box sx={{ color: "#ec7777" }}><TikTokIcon fontSize={36} /></Box>,
  star:           <StarIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  emoji_events:   <EmojiEventsIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  favorite:       <FavoriteIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  campaign:       <CampaignIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  open_in_new:    <OpenInNewIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  sports_esports: <SportsEsportsIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  share:          <ShareIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
};

function TikTokIcon({ fontSize = 36 }: { fontSize?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={fontSize} height={fontSize} fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5
               2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01
               a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34
               6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
    </svg>
  );
}

interface PlayerInfo {
  name: string;
  score: number;
}

export default function AdminResultsPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const [resultCards, setResultCards] = useState<ResultCard[]>([]);
  const [players, setPlayers] = useState<Record<string, PlayerInfo>>({});
  const [lobbyKey, setLobbyKey] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [turnHistory, setTurnHistory] = useState<TurnInfo[]>([]);
  const [gamePlayers, setGamePlayers] = useState<Record<string, Player>>({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [ctaCards, setCtaCards] = useState<CtaCard[]>([]);

  useEffect(() => { CommunityApi.getCtaCards().then(setCtaCards); }, []);

  // Feedback state
  const messageInputEl = useRef<HTMLTextAreaElement>(null);
  const [sent, setSent] = useState(false);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const adminSecret = sessionStorage.getItem("admin-secret");
    if (!adminSecret) {
      setError("No admin secret found. Open the admin panel first to authenticate.");
      setLoading(false);
      return;
    }

    fetch(serverUrl(`/api/admin/match-history/${id}/result-cards`), {
      headers: { "x-admin-secret": adminSecret },
    })
      .then((resp) => {
        if (!resp.ok) throw new Error(resp.status === 401 ? "Unauthorized" : "Failed to load");
        return resp.json();
      })
      .then((data) => {
        setResultCards(data.resultCards ?? []);
        setPlayers(data.players ?? {});
        setLobbyKey(data.lobbyKey ?? "");
        setTurnHistory(data.turnHistory ?? []);
        setGamePlayers(data.gamePlayers ?? {});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const playerNames = Object.values(players).map((p) => p.name);
  const hasCards = resultCards.length > 0;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      id="box1"
      sx={{
        position: { xs: "relative", md: "auto" },
        minHeight: "100vh",
        overflow: "auto",
        paddingBottom: "2em",
      }}
    >
      <Stack justifyContent="flex-start" alignItems={"center"} spacing={3} sx={{ paddingTop: "2em", paddingInline: "1em" }}>

        {/* Header */}
        <Stack alignItems={"center"}>
          <Typography variant="h1" sx={{ fontSize: 26 }}>
            {t("thanks_for_playing")}
          </Typography>
          <Typography variant="h1" sx={{ fontSize: 26 }}>
            {t("more_stuff")}
          </Typography>
        </Stack>

        {/* Result cards section */}
        {hasCards && <ResultCardGrid cards={resultCards} playerNames={playerNames} />}

        {/* CTA cards */}
        {ctaCards.length > 0 && (
          <Box sx={{ width: "100%", maxWidth: "900px" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {ctaCards.map((card) => (
                <a
                  key={card.id}
                  href={card.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ flex: 1, textDecoration: "none", display: "flex" }}
                  onClick={() => track("cta_click", { cardId: card.id, cardTitle: card.title, link: card.link })}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2.5, borderRadius: 3, display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 1, cursor: "pointer", flex: 1,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": { transform: "translateY(-3px)", boxShadow: 5 },
                    }}
                  >
                    {ICON_MAP[card.icon] ?? <StyleIcon sx={{ fontSize: 36, color: "#ec7777" }} />}
                    <Typography variant="h6" fontWeight="bold" textAlign="center">{card.title}</Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">{card.body}</Typography>
                  </Paper>
                </a>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ width: "100%", maxWidth: "900px" }} />

        {/* Review Game button */}
        {turnHistory.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<RateReviewIcon />}
            onClick={() => setReviewOpen(true)}
          >
            Review Full Game
          </Button>
        )}

        {/* Feedback */}
        <Paper
          sx={{
            width: { xs: "90%", md: "50vw" },
            padding: ".5em",
          }}
        >
          {!sent ? (
            <>
              <Stack
                alignItems={"center"}
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Typography variant="h6" sx={{ fontSize: 18 }}>
                  <Trans components={{ h: <span className="highlightedText" /> }}>
                    {t("quickFeedBackQuestion")}
                  </Trans>
                </Typography>
                <Stack
                  direction="row"
                  alignItems={"center"}
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                >
                  <TextField
                    onChange={() => {
                      setValid((messageInputEl.current?.value as unknown as string) !== "");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && valid) {
                        e.preventDefault();
                        Feedback.submitFeedback(
                          messageInputEl.current?.value as unknown as string,
                          "Admin",
                          lobbyKey || undefined
                        );
                        setSent(true);
                      }
                    }}
                    placeholder={t("quick_feedback")}
                    className="quickFeedbackText"
                    inputProps={{ ref: messageInputEl, maxLength: 2000 }}
                    sx={{ height: "100%", width: "100%" }}
                    multiline
                    rows={4}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          disabled={!valid}
                          onClick={() => {
                            Feedback.submitFeedback(
                              messageInputEl.current?.value as unknown as string,
                              "Admin",
                              lobbyKey || undefined
                            );
                            setSent(true);
                          }}
                        >
                          <BiMailSend fontSize={25}></BiMailSend>
                        </IconButton>
                      ),
                    }}
                  />
                </Stack>
              </Stack>
            </>
          ) : (
            <Stack alignItems={"center"} justifyContent="center" sx={{ height: "100%" }}>
              <Typography variant="h6" sx={{ fontSize: 18 }}>
                {t("thx4Impro")}
              </Typography>
            </Stack>
          )}
        </Paper>

        {/* Restart / Leave buttons */}
        <Grid container columns={3} justifyContent={"row-reverse"} sx={{ width: { xs: "90%", md: "50vw" } }}>
          <Grid item xs={1} />
          <Grid item xs={1}>
            <Stack alignItems={"center"}>
              <Button variant="contained" onClick={() => history.push("/play")}>
                {t("restart_lobby")}
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={1}>
            <Stack sx={{ alignItems: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => history.push("/play")}
              >
                <GiEntryDoor fontSize={25} />
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      {/* Game Review Dialog */}
      <GameReview
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        turnHistory={turnHistory}
        players={gamePlayers}
      />
    </Box>
  );
}
